<template>
    <div class="health-clinic-records">
        <v-offline
            online-class="online"
            offline-class="offline"
            @detected-condition="amIOnline"
        >
            <template v-slot:[onlineSlot] :slot-name="onlineSlot">
                {{ onlineOfflineMsg }}
            </template>
            <template v-slot:[offlineSlot] :slot-name="offlineSlot">
                {{ onlineOfflineMsg }}
            </template>
        </v-offline>

        <div class="row">
            <div class="col-md-12 card">
                <div class="card-body">
                    <div class="row set_row">
                        <div class="col-sm-12 col-xl-12 set_table_center">
                            <div class="table-responsive">
                                <table
                                    id="hcRecords"
                                    class="display table-striped table-bordered select"
                                    cellspacing="0"
                                    width="100%"
                                >
                                    <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                id="allCheckbox"
                                                @change="handleSelectAll"
                                                :disabled="isReadOnly"
                                            >
                                        </th>
                                        <th>Checkup Date/Time</th>
                                        <th>Collected by</th>
                                        <th>Community</th>
                                        <th>Photo</th>
                                        <th>Collar ID</th>
                                        <th>Gender</th>
                                        <th>Age</th>
                                        <th>Treatment</th>
                                        <th>Deworming</th>
                                        <th>Vaccination</th>
                                        <th>Other Treatment</th>
                                        <th>Owner Name</th>
                                        <th>Owner Number</th>
                                        <th>Owner Location</th>
                                        <th>Action</th>
                                    </tr>
                                    </thead>
                                </table>
                            </div>

                            <div class="mt-3" v-if="!isReadOnly">
                                <button
                                    type="button"
                                    class="btn btn-danger"
                                    @click="archiveRecords"
                                    :disabled="isArchiving"
                                >
                                    <span v-if="isArchiving">
                                        <i class="fa fa-spinner fa-spin"></i> Archiving...
                                    </span>
                                    <span v-else>
                                        <i class="fa fa-archive"></i> Archive Selected
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Survey Details Modal -->
        <el-dialog
            width="80%"
            title="Survey Details"
            :visible.sync="dialogs.recordSurvey"
            :close-on-click-modal="false"
        >
            <div
                class="hc-record-survey px-3 mb-4"
                v-for="(survey, index) in surveyData"
                :key="index"
            >
                <div class="row mb-2">
                    <div class="col-12">
                        <strong>({{ index + 1 }}) {{ survey.question }}</strong>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12">
                        <ul class="list-unstyled">
                            <li
                                v-for="(availableOption, optionIndex) in survey.available_options"
                                :key="optionIndex"
                                :class="{ 'selected-option': availableOption === survey.selected_option }"
                                class="mb-1"
                            >
                                <i
                                    :class="availableOption === survey.selected_option ? 'fa fa-check-circle text-success' : 'fa fa-circle-o'"
                                    class="mr-2"
                                ></i>
                                {{ availableOption }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import mixin from '../../../mixins/GlobalMixin';
import {Dialog} from "element-ui";
import VOffline from 'v-offline';
import healthClinicService from "../../../services/health-clinic.service";
import Swal from 'sweetalert2';

export default {
    name: 'HealthClinicRecords',

    components: {
        [Dialog.name]: Dialog,
        VOffline
    },

    mixins: [mixin],

    props: {
        projectId: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            isReadOnly: false,
            project: null,
            records: [],
            dialogs: {
                recordSurvey: false
            },
            selectedRecord: {
                id: null
            },
            surveyData: null,
            table: null,
            isArchiving: false,
            selectedRecords: new Set()
        };
    },

    computed: {
        /**
         * Get DataTables configuration
         */
        dataTablesConfig() {
            return {
                processing: true,
                serverSide: true,
                responsive: true,
                searchDelay: 500,
                dom: 'lBfrtip',
                pageLength: 15,
                lengthMenu: [[15, 50, 100, -1], [15, 50, 100, "All"]],
                order: [[1, "desc"]], // Order by checkup date by default
                scrollX: true,
                info: false, // Hide "Showing X to Y of Z entries" text
                language: {
                    processing: "Loading records...",
                    emptyTable: "No health clinic records found",
                    zeroRecords: "No matching records found"
                },
                ajax: {
                    url: `${window.DATATABLES_BASE_URL}health/clinic/records`,
                    type: "POST",
                    data: {
                        project_id: this.projectId
                    },
                    error: (xhr, error, thrown) => {
                        console.error('DataTables AJAX Error:', {xhr, error, thrown});

                        Swal.fire({
                            title: 'Data Loading Error',
                            text: 'Failed to load records. Please check your connection and try again.',
                            icon: 'error',
                            confirmButtonColor: '#dc3545',
                            showCancelButton: true,
                            confirmButtonText: 'Retry',
                            cancelButtonText: 'Close'
                        }).then((result) => {
                            if (result.isConfirmed && this.table) {
                                this.table.ajax.reload();
                            }
                        });
                    }
                },
                columns: this.getColumnDefinitions(),
                buttons: [
                    {
                        extend: 'excelHtml5',
                        text: '<i class="fa fa-file-excel-o"></i> Export to Excel',
                        className: 'btn btn-success btn-sm',
                        exportOptions: {
                            columns: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
                            format: {
                                body: (data, row, column, node) => {
                                    // Export column index 3 is Photo (table column 4) - extract URL from img src
                                    if (column === 3) {
                                        const $data = $(data);
                                        const img = $data.filter('img').length ? $data.filter('img') : $data.find('img');
                                        if (img.length) {
                                            return img.attr('src') || '';
                                        }
                                        return '';
                                    }
                                    // Export column index 13 is Owner Location (table column 14) - extract URL from anchor href
                                    if (column === 13) {
                                        const $data = $(data);
                                        const link = $data.filter('a').length ? $data.filter('a') : $data.find('a');
                                        if (link.length) {
                                            return link.attr('href') || '';
                                        }
                                        return '';
                                    }
                                    // For all other columns, return the text content
                                    return data;
                                }
                            }
                        }
                    },
                    {
                        extend: 'colvis',
                        text: '<i class="fa fa-columns"></i> Column Visibility',
                        className: 'btn btn-info btn-sm'
                    }
                ],
                drawCallback: () => {
                    this.initializeRowEvents();
                },
                initComplete: () => {
                    console.log('DataTable initialized successfully');
                }
            };
        }
    },

    methods: {
        /**
         * Initialize the component
         */
        init() {
            this.$store.dispatch("setScreenName", "Health Clinic Records");
            this.initializeDataTable();
        },

        /**
         * Get column definitions for DataTables
         */
        getColumnDefinitions() {
            return [
                // Column 0: Checkbox
                {
                    targets: 0,
                    orderable: false,
                    searchable: false,
                    className: 'text-center',
                    width: '50px',
                    render: (data, type, full) => {
                        if (this.isReadOnly) return '';
                        return `<input type="checkbox" class="record-checkbox" value="${full[0]}" data-record-id="${full[0]}">`;
                    }
                },
                // Column 1: Checkup Date/Time
                {
                    targets: 1,
                    render: (data, type, full) => {
                        return this.formatDateTime(data);
                    }
                },
                // Column 2: Collected by
                {
                    targets: 2
                },
                 // Column 3: community
                {
                    targets: 3
                },
                // Column 4: Photo
                {
                    targets: 4,
                    orderable: false,
                    searchable: false,
                    className: 'text-center',
                    render: (data, type, full) => {
                        if (!data) return '<span class="text-muted">No Photo</span>';
                        return `<img src="${data}" class="datatable_image_thumbnail" style="cursor: pointer; object-fit: cover;" height="60" width="60" alt="Record Photo">`;
                    }
                },
                // Column 5: Collar ID
                {
                    targets: 5
                },
                // Column 6: Gender
                {
                    targets: 6
                },
                // Column 7: Age
                {
                    targets: 7
                },
                // Column 8: Treatment
                {
                    targets: 8
                },
                // Column 9: Deworming
                {
                    targets: 9
                },
                // Column 10: Vaccination
                {
                    targets: 10
                },
                // Column 11: Other Treatment
                {
                    targets: 11
                },
                // Column 12: Owner Name
                {
                    targets: 12
                },
                // Column 13: Owner Number
                {
                    targets: 13
                },
                // Column 14: Owner Location
                {
                    targets: 14,
                    searchable: false,
                    orderable: false,
                    className: 'text-center',
                    render: (data, type, full) => {
                        if (!data) return '<span class="text-muted">No Location</span>';
                        return `<a href="${data}" target="_blank" class="btn btn-sm btn-outline-info text-white">
                                   <i class="fa fa-map-marker"></i> Map
                                 </a>`;
                    }
                },
                // Column 15: Actions
                {
                    targets: 15,
                    searchable: false,
                    orderable: false,
                    className: 'text-center',
                    width: '150px',
                    render: (data, type, full) => {
                        const recordId = data;
                        let buttons = '';

                        if (this.isReadOnly) {
                            buttons = `<button type="button" class="btn btn-info btn-sm view-record-btn mb-1" data-record-id="${recordId}">
                                         <i class="fa fa-eye"></i> View
                                       </button>`;
                        } else {
                            buttons = `<button type="button" class="btn btn-warning btn-sm view-record-btn mb-1" data-record-id="${recordId}">
                                         <i class="fa fa-edit"></i> View/Edit
                                       </button>`;
                        }

                        buttons += `<br><button type="button" class="btn btn-info btn-sm view-survey-btn" data-record-id="${recordId}">
                                      <i class="fa fa-list-alt"></i> View Survey
                                    </button>`;

                        return buttons;
                    }
                }
            ];
        },

        /**
         * Initialize DataTable
         */
        initializeDataTable() {
            if (this.table) {
                this.table.destroy();
                this.table = null;
            }

            this.$nextTick(() => {
                try {
                    this.table = $("#hcRecords").DataTable(this.dataTablesConfig);
                } catch (error) {
                    console.error('Failed to initialize DataTable:', error);
                    this.showAlert('Error', 'Failed to initialize data table. Please refresh the page.', false);
                }
            });
        },

        /**
         * Initialize row-specific events after table draw
         */
        initializeRowEvents() {
            // Image zoom functionality
            $('.datatable_image_thumbnail').off('click').on('click', function () {
                // Implement image zoom modal if needed
                const imageUrl = $(this).attr('src');
                window.open(imageUrl, '_blank');
            });

            // View/Edit record buttons
            $('.view-record-btn').off('click').on('click', (e) => {
                const recordId = $(e.target).closest('button').data('record-id');
                this.navigateToRecord(recordId);
            });

            // View survey buttons
            $('.view-survey-btn').off('click').on('click', (e) => {
                const recordId = $(e.target).closest('button').data('record-id');
                this.viewRecordSurvey(recordId);
            });

            // Checkbox events
            $('.record-checkbox').off('change').on('change', (e) => {
                this.handleRecordSelection(e);
            });
        },

        /**
         * Navigate to record view/edit page
         */
        navigateToRecord(recordId) {
            this.$router.push({
                name: 'hcRecordView',
                params: {recordId: recordId}
            });
        },

        /**
         * Handle record checkbox selection
         */
        handleRecordSelection(event) {
            const recordId = event.target.value;
            const isChecked = event.target.checked;

            if (isChecked) {
                this.selectedRecords.add(recordId);
            } else {
                this.selectedRecords.delete(recordId);
                // Uncheck "select all" if individual item is unchecked
                $('#allCheckbox')[0].checked = false;
            }
        },

        /**
         * Handle select all checkbox
         */
        handleSelectAll(event) {
            const isChecked = event.target.checked;

            $('.record-checkbox').each((index, checkbox) => {
                checkbox.checked = isChecked;
                const recordId = checkbox.value;

                if (isChecked) {
                    this.selectedRecords.add(recordId);
                } else {
                    this.selectedRecords.delete(recordId);
                }
            });
        },

        /**
         * View survey details for a record
         */
        async viewRecordSurvey(recordId) {
            this.selectedRecord.id = recordId;

            // Show loading with utility method
            this.showLoadingDialog('Loading Survey Details', 'Please wait...');

            try {
                const apiResponse = await healthClinicService.getRecordSurveyDetails(recordId);

                Swal.close(); // Close loading dialog

                if (!apiResponse.response.error.error_code) {
                    this.surveyData = apiResponse.response.data.survey_response_data;

                    if (this.surveyData && this.surveyData.length > 0) {
                        this.dialogs.recordSurvey = true;
                    } else {
                        await Swal.fire({
                            title: 'No Survey Data',
                            text: 'No survey data is available for this record.',
                            icon: 'info',
                            confirmButtonColor: '#17a2b8'
                        });
                    }
                } else {
                    await this.showErrorDialog(
                        'Error',
                        apiResponse.response.error.msg || 'Failed to load survey details.'
                    );
                }
            } catch (error) {
                console.error('Survey fetch error:', error);
                Swal.close(); // Close loading dialog

                await this.showErrorDialog(
                    'Network Error',
                    'Failed to load survey details. Please check your connection and try again.'
                );
            }
        },

        /**
         * Archive selected records using SweetAlert2
         */
        async archiveRecords() {
            const selectedIds = Array.from(this.selectedRecords);

            if (selectedIds.length === 0) {
                await Swal.fire({
                    title: 'No Selection',
                    text: 'Please select records to archive.',
                    icon: 'info',
                    confirmButtonColor: '#17a2b8'
                });
                return;
            }

            // Use utility method for confirmation
            const result = await this.showConfirmDialog(
                'Archive Records?',
                `Are you sure you want to archive ${selectedIds.length} record${selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.`,
                `Yes, archive ${selectedIds.length > 1 ? 'them' : 'it'}!`
            );

            if (!result.isConfirmed) return;

            this.isArchiving = true;

            // Show processing dialog
            this.showLoadingDialog('Archiving Records...', 'Please wait while we archive the selected records.');

            try {
                const apiResponse = await healthClinicService.archiveRecords({ids: selectedIds});

                if (apiResponse.response.error.error_code === 0) {
                    // Success message
                    await this.showSuccessDialog(
                        'Success!',
                        `${selectedIds.length} record${selectedIds.length > 1 ? 's have' : ' has'} been archived successfully.`
                    );

                    this.selectedRecords.clear();
                    $('#allCheckbox')[0].checked = false;
                    this.table.ajax.reload(); // Reload table data
                } else {
                    // Error from API
                    await this.showErrorDialog(
                        'Error!',
                        apiResponse.response.error.error_msg || 'Failed to archive records.'
                    );
                }
            } catch (error) {
                console.error('Archive error:', error);

                // Network or other error
                await this.showErrorDialog(
                    'Error!',
                    'Failed to archive records. Please check your connection and try again.'
                );
            } finally {
                this.isArchiving = false;
            }
        },

        /**
         * Format date/time for display
         */
        formatDateTime(dateTime) {
            if (!dateTime) return 'N/A';

            try {
                const date = new Date(dateTime);
                return date.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } catch (error) {
                return dateTime;
            }
        },

        /**
         * Utility method for showing loading dialog
         */
        showLoadingDialog(title = 'Loading...', text = 'Please wait...') {
            return Swal.fire({
                title,
                text,
                icon: 'info',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
        },

        /**
         * Utility method for showing success message
         */
        showSuccessDialog(title, text, timer = 3000) {
            return Swal.fire({
                title,
                text,
                icon: 'success',
                confirmButtonColor: '#28a745',
                timer,
                timerProgressBar: timer > 0,
                showConfirmButton: timer === 0
            });
        },

        /**
         * Utility method for showing error message
         */
        showErrorDialog(title, text) {
            return Swal.fire({
                title,
                text,
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
        },

        /**
         * Utility method for showing confirmation dialog
         */
        showConfirmDialog(title, text, confirmText = 'Yes', cancelText = 'Cancel') {
            return Swal.fire({
                title,
                text,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: confirmText,
                cancelButtonText: cancelText,
                reverseButtons: true,
                focusCancel: true
            });
        }
    },

    mounted() {
        this.project = this.$store.getters.getCurrentSelectedProject;

        // Check user permissions
        this.isReadOnly = false;
        this.$store.getters.getUserRoleForCurrentProject.forEach(role => {
            if (role.role_name === 'VIEWER') {
                this.isReadOnly = true;
            }
        });

        this.init();
    },

    beforeDestroy() {
        if (this.table) {
            this.table.destroy();
            this.table = null;
        }
    }
};
</script>

<style lang="scss" scoped>
.health-clinic-records {
    .hc-record-survey {
        .selected-option {
            color: #28a745;
            font-weight: bold;
        }
    }

    .badge-pink {
        background-color: #e91e63;
        color: white;
    }

    .table-responsive {
        border-radius: 0.375rem;
        box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    }

    .btn {
        margin: 0.125rem;
    }

    .datatable_image_thumbnail {
        border-radius: 0.25rem;
        transition: transform 0.2s ease-in-out;

        &:hover {
            transform: scale(1.1);
        }
    }
}
</style>
